/**
 * Save schema and codec. The GDD rule is seed + edits, never terrain: chunks
 * regenerate deterministically from the seed, so persisting generated bytes
 * would only add size and corruption surface. `bestDepth` is the leaderboard
 * stat and survives across sessions.
 *
 * Bump `SAVE_VERSION` on any shape change and add a migration branch in the
 * same commit. `migrate` never trusts its input: the save has been through
 * IndexedDB, a cloud sync, and possibly a player with devtools open, so every
 * field is validated and absurd values are clamped rather than trusted.
 */

import type { SaveCodec } from "@tokamio/sdk";
import { BLOCKS } from "../data/blocks";
import { WORLD_CEILING, WORLD_FLOOR } from "../data/worldgen";
import type { World } from "./world";

/** Bump on every shape change and add a branch to `migrate` in the same commit. */
export const SAVE_VERSION = 1;

export interface SaveData {
  version: number;
  /** World seed. 0 is the default; the boot picks a fresh seed for a new player. */
  seed: number;
  playerPos: [number, number, number];
  /** Edit overlay as [blockKey, blockId] pairs, matching `World.edits`. */
  edits: Array<[string, number]>;
  bestDepth: number;
  runsPlayed: number;
  settings: { muted: boolean; reducedMotion: boolean };
}

/** Hard caps so a corrupt save cannot exhaust memory or place the player on the moon. */
const MAX_EDITS = 100_000;
const MAX_RUNS = 1_000_000;
const MAX_DEPTH = -WORLD_FLOOR;
const MAX_HORIZONTAL = 1_000_000;
const DEFAULT_POS: [number, number, number] = [8, 32, 8];
const EDIT_KEY = /^-?\d+,-?\d+,-?\d+$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validSeed(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.floor(value) >>> 0 : 0;
}

function clampInt(value: unknown, max: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.min(Math.floor(value), max)
    : 0;
}

function validPos(value: unknown): [number, number, number] {
  if (Array.isArray(value) && value.length >= 3 && value.every((n) => typeof n === "number" && Number.isFinite(n))) {
    const [x, y, z] = value as [number, number, number];
    const horizontal = (n: number): number => Math.max(-MAX_HORIZONTAL, Math.min(MAX_HORIZONTAL, n));
    return [horizontal(x), Math.max(WORLD_FLOOR, Math.min(WORLD_CEILING, y)), horizontal(z)];
  }
  return [...DEFAULT_POS];
}

function validEdits(value: unknown): Array<[string, number]> {
  if (!Array.isArray(value)) return [];
  const clean = new Map<string, number>();
  for (const entry of value.slice(0, MAX_EDITS)) {
    if (!Array.isArray(entry) || entry.length !== 2) continue;
    const [key, id] = entry as [unknown, unknown];
    if (typeof key !== "string" || !EDIT_KEY.test(key)) continue;
    if (typeof id !== "number" || !Number.isInteger(id) || id < 0 || id >= BLOCKS.length) continue;
    clean.set(key, id);
  }
  return [...clean.entries()];
}

export const saveCodec: SaveCodec<SaveData> = {
  version: SAVE_VERSION,

  create: () => ({
    version: SAVE_VERSION,
    seed: 0,
    playerPos: [...DEFAULT_POS],
    edits: [],
    bestDepth: 0,
    runsPlayed: 0,
    settings: { muted: false, reducedMotion: false },
  }),

  migrate(raw, fromVersion) {
    // v1 is the first schema, so there are no older branches yet. When v2
    // lands, add `fromVersion < 2` backfills here, before validation.
    void fromVersion;
    const data = isRecord(raw) ? raw : {};
    const settings = isRecord(data.settings) ? data.settings : {};
    return {
      version: SAVE_VERSION,
      seed: validSeed(data.seed),
      playerPos: validPos(data.playerPos),
      edits: validEdits(data.edits),
      bestDepth: clampInt(data.bestDepth, MAX_DEPTH),
      runsPlayed: clampInt(data.runsPlayed, MAX_RUNS),
      settings: {
        muted: Boolean(settings.muted),
        reducedMotion: Boolean(settings.reducedMotion),
      },
    };
  },
};

/**
 * Snapshot a world into save shape. `previous` carries the cross-session
 * fields the world does not own (best depth across runs, run counter,
 * settings); pass the currently open save when one exists.
 */
export function saveFromWorld(world: World, previous?: SaveData): SaveData {
  return {
    version: SAVE_VERSION,
    seed: world.seed,
    playerPos: [world.position.x, world.position.y, world.position.z],
    edits: [...world.edits.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
    bestDepth: Math.max(previous?.bestDepth ?? 0, world.stats.maxDepth),
    runsPlayed: previous?.runsPlayed ?? 0,
    settings: previous?.settings ?? { muted: false, reducedMotion: false },
  };
}

/**
 * Load a save into an existing world. The caller creates the world with
 * `createWorld(data.seed)` first — seed, chunk store and rng are not touched
 * here, and terrain needs no invalidation because edits are an overlay.
 */
export function applySaveToWorld(world: World, data: SaveData): void {
  [world.position.x, world.position.y, world.position.z] = data.playerPos;
  world.edits.clear();
  for (const [key, id] of data.edits) world.edits.set(key, id);
  world.stats.maxDepth = data.bestDepth;
  world.stats.edits = data.edits.length;
}
